import React, { useState } from 'react';

const SECTIONS = [
  {
    id: 'getting-started',
    icon: '🚀',
    title: 'सुरुवात / Getting Started',
    content: GettingStarted,
  },
  {
    id: 'dashboard',
    icon: '📊',
    title: 'डॅशबोर्ड / Dashboard',
    content: DashboardDoc,
  },
  {
    id: 'households',
    icon: '🏠',
    title: 'कुटुंबे / Households',
    content: HouseholdsDoc,
  },
  {
    id: 'citizens',
    icon: '👥',
    title: 'नागरिक / Citizens',
    content: CitizensDoc,
  },
  {
    id: 'certificates',
    icon: '📄',
    title: 'प्रमाणपत्रे / Certificates',
    content: CertificatesDoc,
  },
  {
    id: 'complaints',
    icon: '📢',
    title: 'तक्रारी / Complaints',
    content: ComplaintsDoc,
  },
  {
    id: 'schemes',
    icon: '🎯',
    title: 'योजना / Schemes',
    content: SchemesDoc,
  },
  {
    id: 'gramsabha',
    icon: '🏛️',
    title: 'ग्रामसभा / Gramsabha',
    content: GramsabhaDoc,
  },
  {
    id: 'finance',
    icon: '💰',
    title: 'वित्त / Finance',
    content: FinanceDoc,
  },
  {
    id: 'projects',
    icon: '🏗️',
    title: 'प्रकल्प / Projects',
    content: ProjectsDoc,
  },
  {
    id: 'content',
    icon: '🌐',
    title: 'वेबसाईट / Website Content',
    content: ContentDoc,
  },
  {
    id: 'facilities',
    icon: '🏥',
    title: 'सुविधा / Facilities',
    content: FacilitiesDoc,
  },
  {
    id: 'businesses',
    icon: '🏪',
    title: 'व्यवसाय / Businesses',
    content: BusinessesDoc,
  },
  {
    id: 'departments-staff',
    icon: '🏢',
    title: 'विभाग व कर्मचारी / Departments & Staff',
    content: DeptStaffDoc,
  },
  {
    id: 'user-approvals',
    icon: '🛡️',
    title: 'वापरकर्ता मंजुरी / User Approvals',
    content: UserApprovalsDoc,
  },
  {
    id: 'rbac',
    icon: '🔐',
    title: 'भूमिका व परवानग्या / Roles & Permissions',
    content: RBACDoc,
  },
];

export default function AdminDocumentation() {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSteps, setExpandedSteps] = useState({});

  const toggleStep = (key) => {
    setExpandedSteps((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = searchQuery.trim()
    ? SECTIONS.filter(
        (s) =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.id.includes(searchQuery.toLowerCase())
      )
    : SECTIONS;

  const ActiveContent = SECTIONS.find((s) => s.id === activeSection)?.content;

  return (
    <>
      <style>{styles}</style>
      <div className="doc-page">
        {/* Header */}
        <div className="doc-header">
          <div className="doc-header-left">
            <span className="doc-header-icon">📖</span>
            <div>
              <h1>प्रशासन मार्गदर्शक / Admin Guide</h1>
              <p>Digital Grampanchayat Portal — Complete User Manual</p>
            </div>
          </div>
          <div className="doc-search-wrap">
            <input
              type="text"
              placeholder="🔍 Search modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="doc-search"
            />
          </div>
        </div>

        <div className="doc-layout">
          {/* Sidebar Nav */}
          <nav className="doc-nav">
            {filtered.map((s) => (
              <button
                key={s.id}
                className={`doc-nav-btn ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => { setActiveSection(s.id); setSearchQuery(''); }}
              >
                <span className="doc-nav-icon">{s.icon}</span>
                <span className="doc-nav-label">{s.title}</span>
              </button>
            ))}
          </nav>

          {/* Content Area */}
          <div className="doc-content">
            {ActiveContent ? (
              <ActiveContent toggleStep={toggleStep} expandedSteps={expandedSteps} />
            ) : (
              <div className="doc-empty">Select a module from the sidebar</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Reusable Components ─── */

function StepCard({ number, title, children, id, toggleStep, expandedSteps }) {
  const isOpen = expandedSteps?.[id] ?? true;
  return (
    <div className="doc-step">
      <div className="doc-step-header" onClick={() => toggleStep?.(id)}>
        <span className="doc-step-num">{number}</span>
        <span className="doc-step-title">{title}</span>
        <span className="doc-step-toggle">{isOpen ? '▾' : '▸'}</span>
      </div>
      {isOpen && <div className="doc-step-body">{children}</div>}
    </div>
  );
}

function InfoBox({ type = 'info', children }) {
  const colors = { info: '#e3f2fd', tip: '#e8f5e9', warn: '#fff8e1', important: '#fce4ec' };
  const borders = { info: '#90caf9', tip: '#a5d6a7', warn: '#ffe082', important: '#ef9a9a' };
  const icons = { info: 'ℹ️', tip: '💡', warn: '⚠️', important: '🔴' };
  return (
    <div style={{ background: colors[type], border: `1px solid ${borders[type]}`, borderRadius: 6, padding: '8px 12px', margin: '8px 0', fontSize: 13, lineHeight: 1.5 }}>
      {icons[type]} {children}
    </div>
  );
}

function WorkflowDiagram({ steps }) {
  return (
    <div className="doc-workflow">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div className="doc-wf-step">
            <div className="doc-wf-dot" style={{ background: step.color || '#1565c0' }} />
            <div>
              <strong>{step.label}</strong>
              {step.desc && <div className="doc-wf-desc">{step.desc}</div>}
            </div>
          </div>
          {i < steps.length - 1 && <div className="doc-wf-arrow">↓</div>}
        </React.Fragment>
      ))}
    </div>
  );
}

function FeatureTable({ rows }) {
  return (
    <table className="doc-table">
      <thead>
        <tr>
          <th>Feature</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td><strong>{r[0]}</strong></td>
            <td>{r[1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─── Section Content Components ─── */

function GettingStarted({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🚀 सुरुवात कशी करावी / Getting Started</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>
        Digital Grampanchayat Portal मध्ये आपले स्वागत आहे! हे मार्गदर्शक तुम्हाला प्रणालीच्या सर्व वैशिष्ट्यांशी परिचित करेल.
        <br />Welcome to the Digital Grampanchayat Portal! This guide will walk you through all system features.
      </p>

      <StepCard number="1" title="Login to Admin Portal" id="gs-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ul>
          <li>Go to your Grampanchayat public website</li>
          <li>Click <strong>"Login"</strong> at the top</li>
          <li>Enter your email/phone and password</li>
          <li>You'll be redirected to the Admin Dashboard</li>
        </ul>
        <InfoBox type="tip">Your account must be approved by an existing admin before you can login.</InfoBox>
      </StepCard>

      <StepCard number="2" title="Understanding the Sidebar" id="gs-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <p>The left sidebar shows all modules you have access to. Modules you don't have permission for are automatically hidden.</p>
        <FeatureTable rows={[
          ['📊 Dashboard', 'Overview stats and analytics'],
          ['🏠 Households', 'Census & household records'],
          ['👥 Citizens', 'Citizen database management'],
          ['📄 Certificates', 'Certificate applications & issuance'],
          ['📢 Complaints', 'Complaint tracking & resolution'],
          ['🎯 Schemes', 'Government schemes & applications'],
          ['🏛️ Gramsabha', 'Meeting management & resolutions'],
          ['💰 Finance', 'Budget, income & expenses'],
          ['🏗️ Projects', 'Development project tracking'],
          ['🌐 Website Content', 'Public website management'],
          ['📋 Notices', 'Public notices & announcements'],
          ['🏥 Facilities', 'Public facility management'],
          ['🏪 Businesses', 'Business registration & tracking'],
          ['🏢 Departments', 'Department management'],
          ['👔 Staff Members', 'Staff records & assignments'],
          ['🛡️ User Approvals', 'Approve/reject new registrations'],
        ]} />
      </StepCard>

      <StepCard number="3" title="Common Operations" id="gs-3" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ul>
          <li><strong>Search & Filter:</strong> Most modules have search bars and filter dropdowns at the top</li>
          <li><strong>Create New:</strong> Click the <strong>"+ Add New"</strong> or <strong>"Create"</strong> button</li>
          <li><strong>Edit:</strong> Click the <strong>"✏️ Edit"</strong> button on any row</li>
          <li><strong>View Details:</strong> Click a row or the <strong>"👁️ View"</strong> button</li>
          <li><strong>Pagination:</strong> Use Previous/Next at the bottom of lists</li>
        </ul>
        <InfoBox type="info">All changes are saved immediately. There is no separate "Save" step after status changes.</InfoBox>
      </StepCard>
    </div>
  );
}

function DashboardDoc() {
  return (
    <div>
      <h2>📊 डॅशबोर्ड / Dashboard</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>The dashboard provides a real-time overview of your Grampanchayat data.</p>
      <h3>Key Metrics</h3>
      <FeatureTable rows={[
        ['Total Households', 'Number of registered households in the GP'],
        ['Total Citizens', 'Number of registered citizens'],
        ['Certificate Applications', 'Total certificate requests received'],
        ['Total Complaints', 'Number of complaints filed by citizens'],
      ]} />
      <h3>Analytics Views</h3>
      <ul>
        <li><strong>Certificates by Status:</strong> Breakdown of submitted, under review, approved, rejected, issued</li>
        <li><strong>Complaints by Status:</strong> Filed, acknowledged, assigned, in-progress, resolved, closed</li>
        <li><strong>Ward-wise Distribution:</strong> Households per ward number</li>
        <li><strong>Financial Summary:</strong> Total income, expenses, and net balance</li>
        <li><strong>Citizen Demographics:</strong> Gender distribution and age groups</li>
      </ul>
      <InfoBox type="tip">Dashboard data refreshes automatically when you navigate to it.</InfoBox>
    </div>
  );
}

function HouseholdsDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏠 कुटुंबे / Households</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Manage household census records with infrastructure and demographic data.</p>

      <StepCard number="1" title="Add a New Household" id="hh-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ Add Household"</strong></li>
          <li>Fill in: Head Name, Ward Number, Address</li>
          <li>Select House Type: <em>Pucca, Semi-Pucca, or Kutcha</em></li>
          <li>Select Income Group: <em>APL, BPL, or EWS</em></li>
          <li>Enter member counts (Total, Male, Female)</li>
          <li>Toggle infrastructure: Electricity, Water Supply, Toilet</li>
          <li>Optionally add GPS coordinates</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Search & Filter Households" id="hh-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ul>
          <li><strong>Search:</strong> Type household head name or UID in the search box</li>
          <li><strong>Filter:</strong> Select income group (APL/BPL/EWS) from dropdown</li>
          <li>Results update in real-time with pagination (20 per page)</li>
        </ul>
      </StepCard>

      <h3>Data Fields</h3>
      <FeatureTable rows={[
        ['Head Name', 'Name of the household head'],
        ['Ward Number', 'Ward number within the GP'],
        ['House Type', 'Pucca / Semi-Pucca / Kutcha'],
        ['Income Group', 'APL / BPL / EWS'],
        ['Members', 'Total, Male, Female member count'],
        ['Infrastructure', 'Electricity, Water, Toilet availability'],
        ['GPS', 'Latitude & longitude (optional)'],
      ]} />
    </div>
  );
}

function CitizensDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>👥 नागरिक / Citizens</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Maintain a complete database of citizens with identity and demographic information.</p>

      <StepCard number="1" title="Register a Citizen" id="cz-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ Add Citizen"</strong></li>
          <li>Enter: First Name, Last Name, Date of Birth</li>
          <li>Select Gender and enter Aadhaar / Voter ID</li>
          <li>Add Phone, Education, Occupation, Blood Group</li>
          <li>Check "Handicapped" if applicable</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Analytics Overview" id="cz-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <p>At the top of the Citizens page, you'll see:</p>
        <ul>
          <li><strong>Total Citizens</strong> — count of all registered citizens</li>
          <li><strong>Gender Distribution</strong> — Male ♂️, Female ♀️, Other ⚧️ counts</li>
          <li><strong>Age Groups</strong> — distribution across age brackets</li>
        </ul>
      </StepCard>

      <InfoBox type="info">Citizens can also self-register through the public portal. Those registrations go through the <strong>User Approvals</strong> module.</InfoBox>
    </div>
  );
}

function CertificatesDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>📄 प्रमाणपत्रे / Certificates</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Process certificate applications from submission to issuance.</p>

      <h3>Certificate Types</h3>
      <p>Birth, Death, Marriage, Residence, Income, Caste, Domicile, No Objection, Land, Character, Other</p>

      <h3>Status Workflow</h3>
      <WorkflowDiagram steps={[
        { label: 'Submitted', desc: 'Citizen submits application', color: '#9e9e9e' },
        { label: 'Under Review', desc: 'Admin begins verification', color: '#ff9800' },
        { label: 'Approved / Rejected', desc: 'Admin decision', color: '#2196f3' },
        { label: 'Issued', desc: 'Certificate generated & delivered', color: '#4caf50' },
      ]} />

      <StepCard number="1" title="Create a Certificate Application" id="cert-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ New Application"</strong></li>
          <li>Select certificate type from dropdown</li>
          <li>Enter applicant name, phone, address</li>
          <li>Fill purpose and details</li>
          <li>Set fee amount if applicable</li>
          <li>Click <strong>"Submit"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Process an Application" id="cert-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Find the application in the list (use search/filters)</li>
          <li>Click to view details</li>
          <li>Update status: <strong>Under Review → Approved → Issued</strong></li>
          <li>Add remarks at each step</li>
        </ol>
        <InfoBox type="warn">Once rejected, the application cannot be re-approved. A new application must be filed.</InfoBox>
      </StepCard>
    </div>
  );
}

function ComplaintsDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>📢 तक्रारी / Complaints</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Track and resolve citizen complaints with a complete audit trail.</p>

      <h3>Status Workflow</h3>
      <WorkflowDiagram steps={[
        { label: 'Filed', desc: 'Citizen files a complaint', color: '#9e9e9e' },
        { label: 'Acknowledged', desc: 'Admin acknowledges receipt', color: '#2196f3' },
        { label: 'Assigned', desc: 'Assigned to department/person', color: '#ff9800' },
        { label: 'In Progress', desc: 'Work is underway', color: '#ff9800' },
        { label: 'Resolved', desc: 'Issue has been fixed', color: '#4caf50' },
        { label: 'Verified', desc: 'Resolution verified', color: '#4caf50' },
        { label: 'Closed', desc: 'Complaint formally closed', color: '#2e7d32' },
      ]} />

      <InfoBox type="info">Complaints can be <strong>rejected</strong> at most stages, or <strong>reopened</strong> after resolution if the issue persists.</InfoBox>

      <StepCard number="1" title="Process a Complaint" id="comp-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Open a complaint from the list</li>
          <li>Review details: title, description, category, severity</li>
          <li>Use the <strong>Status Update</strong> section to advance the status</li>
          <li>Add resolution notes at each transition</li>
          <li>Set or update the expected resolution date</li>
        </ol>
      </StepCard>

      <StepCard number="2" title="View Complaint History" id="comp-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <p>Each complaint maintains a full audit trail showing:</p>
        <ul>
          <li>Who made each change (name and role)</li>
          <li>What was changed (status, due date, notes)</li>
          <li>Old value → New value</li>
          <li>Timestamp of each action</li>
        </ul>
      </StepCard>

      <h3>Severity Levels</h3>
      <FeatureTable rows={[
        ['Low', 'Minor issues, no urgency'],
        ['Medium', 'Standard priority complaints'],
        ['High', 'Urgent matters needing quick attention'],
        ['Critical', 'Emergency situations requiring immediate action'],
      ]} />
    </div>
  );
}

function SchemesDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🎯 योजना / Schemes</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Manage government schemes and review citizen applications.</p>

      <h3>Dual Tab Interface</h3>
      <FeatureTable rows={[
        ['Schemes Tab', 'Create and view government schemes (Central / State / District level)'],
        ['Applications Tab', 'Review and approve/reject citizen applications for schemes'],
      ]} />

      <StepCard number="1" title="Add a New Scheme" id="sch-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to the <strong>"Schemes"</strong> tab</li>
          <li>Click <strong>"+ Add Scheme"</strong></li>
          <li>Fill: Name, Description, Department, Level</li>
          <li>Set eligibility criteria, benefit amount</li>
          <li>Set date range (Start - End date)</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Review Applications" id="sch-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Switch to the <strong>"Applications"</strong> tab</li>
          <li>Filter by status: Pending / Approved / Rejected</li>
          <li>Click <strong>"Approve"</strong> or <strong>"Reject"</strong> for each application</li>
        </ol>
      </StepCard>
    </div>
  );
}

function GramsabhaDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏛️ ग्रामसभा / Gramsabha</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Schedule village assembly meetings and record resolutions.</p>

      <h3>Meeting Types</h3>
      <FeatureTable rows={[
        ['Regular', 'Scheduled periodic meetings'],
        ['Special', 'Meetings called for specific agenda'],
        ['Emergency', 'Urgent meetings for crisis situations'],
      ]} />

      <StepCard number="1" title="Schedule a Meeting" id="gs-m1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ New Meeting"</strong></li>
          <li>Enter: Title, Type, Date, Time, Venue</li>
          <li>Add Agenda and Description</li>
          <li>Set status (Scheduled)</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Add Resolutions" id="gs-m2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Open a meeting to view details</li>
          <li>Click <strong>"+ Add Resolution"</strong></li>
          <li>Enter: Title, Description, Proposed By</li>
          <li>Record voting: Votes For vs Votes Against</li>
          <li>Click <strong>"Add"</strong></li>
        </ol>
        <InfoBox type="tip">All resolutions are permanently linked to their meeting record for transparency.</InfoBox>
      </StepCard>
    </div>
  );
}

function FinanceDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>💰 वित्त / Finance</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Track budgets, income, and expenses with category-wise breakdowns.</p>

      <h3>Three-Tab Interface</h3>
      <FeatureTable rows={[
        ['Summary', 'Total Income, Expense, Net Balance + category-wise breakdown'],
        ['Budgets', 'Create budget allocations per financial year & category'],
        ['Transactions', 'Record individual income & expense transactions'],
      ]} />

      <StepCard number="1" title="Create a Budget" id="fin-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>"Budgets"</strong> tab</li>
          <li>Click <strong>"+ Create Budget"</strong></li>
          <li>Select Financial Year, Category</li>
          <li>Enter Allocated Amount and Description</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
        <InfoBox type="info">The system automatically tracks spent and remaining amounts.</InfoBox>
      </StepCard>

      <StepCard number="2" title="Record a Transaction" id="fin-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>"Transactions"</strong> tab</li>
          <li>Click <strong>"+ Add Transaction"</strong></li>
          <li>Select Type: Income / Expense</li>
          <li>Enter Category, Amount, Description, Reference Number, Date</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </StepCard>
    </div>
  );
}

function ProjectsDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏗️ प्रकल्प / Projects</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Track development projects from proposal to completion.</p>

      <h3>Project Categories</h3>
      <p>Road, Water, Drainage, Building, Electrification, Sanitation, Education, Health, Agriculture, Digital, Other</p>

      <h3>Status Lifecycle</h3>
      <WorkflowDiagram steps={[
        { label: 'Proposed', desc: 'Project submitted for approval', color: '#9e9e9e' },
        { label: 'Approved', desc: 'Project approved for execution', color: '#2196f3' },
        { label: 'In Progress', desc: 'Work is underway', color: '#ff9800' },
        { label: 'Completed', desc: 'Project finished', color: '#4caf50' },
      ]} />

      <StepCard number="1" title="Create a Project" id="proj-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ Create Project"</strong></li>
          <li>Enter: Title, Description, Category</li>
          <li>Set Estimated Cost, Funding Source</li>
          <li>Set Start Date and Expected End Date</li>
          <li>Click <strong>"Create"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Update Progress" id="proj-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"✏️ Edit"</strong> on a project</li>
          <li>Update Status and Progress percentage (0–100%)</li>
          <li>Update Actual Cost if different from estimate</li>
          <li>Click <strong>"Update"</strong></li>
        </ol>
        <InfoBox type="tip">The progress bar on the project list updates in real-time.</InfoBox>
      </StepCard>
    </div>
  );
}

function ContentDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🌐 वेबसाईट / Website Content</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Manage your public-facing GP website — settings, pages, gallery, notices, fees, representatives, and banners.</p>

      <h3>Seven Tabs</h3>
      <FeatureTable rows={[
        ['GP Settings', 'Sarpanch name, contact info, address, logo, office hours, population'],
        ['Pages', 'Create/edit custom pages with rich HTML editor'],
        ['Gallery', 'Upload and categorize photos/videos'],
        ['Notices', 'Create public notices with priority levels'],
        ['Fees', 'Set service fee amounts'],
        ['Representatives', 'Add elected representatives with photos'],
        ['Banners', 'Manage homepage banner carousel'],
      ]} />

      <StepCard number="1" title="Update GP Settings" id="cnt-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>"GP Settings"</strong> tab</li>
          <li>Update Sarpanch name, About text, Vision</li>
          <li>Set contact info: phone, email, address</li>
          <li>Configure office hours, population data</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Create a Custom Page" id="cnt-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>"Pages"</strong> tab</li>
          <li>Click <strong>"+ Add Page"</strong></li>
          <li>Enter page Title and Slug (URL identifier)</li>
          <li>Use the rich text editor to write content</li>
          <li>Toggle "Published" when ready</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
        <InfoBox type="info">System pages (Home, About, Services, etc.) cannot be deleted but can be edited.</InfoBox>
      </StepCard>

      <StepCard number="3" title="Manage Gallery" id="cnt-3" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>"Gallery"</strong> tab</li>
          <li>Click <strong>"+ Add Image"</strong></li>
          <li>Enter image URL, title, caption</li>
          <li>Select category (Community, Infrastructure, Health, etc.)</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>
    </div>
  );
}

function FacilitiesDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏥 सुविधा / Facilities</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Manage public facilities like health centers, schools, community halls, water points.</p>

      <h3>Facility Types</h3>
      <p>Health, Education, Religious Site, Community Center, Water Facility, Agricultural Facility, Miscellaneous</p>

      <StepCard number="1" title="Add a Facility" id="fac-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ Add Facility"</strong></li>
          <li>Enter Name, select Facility Type</li>
          <li>Add Address, Phone, Opening Hours</li>
          <li>Set Active/Inactive status</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>
    </div>
  );
}

function BusinessesDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏪 व्यवसाय / Businesses</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Register and track local business establishments.</p>

      <StepCard number="1" title="Register a Business" id="biz-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Click <strong>"+ Add Business"</strong></li>
          <li>Enter: Business Name, Owner Name, Business Type</li>
          <li>Add Phone, Address, Registration Number</li>
          <li>Set Verification Status</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>

      <h3>Verification</h3>
      <FeatureTable rows={[
        ['Verified', 'Business has been verified by GP administration'],
        ['Unverified', 'Business registration pending verification'],
      ]} />
      <InfoBox type="tip">Use the filter dropdown to quickly find unverified businesses that need attention.</InfoBox>
    </div>
  );
}

function DeptStaffDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🏢 विभाग व कर्मचारी / Departments & Staff</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Manage organizational departments and their staff members.</p>

      <h3>Departments</h3>
      <StepCard number="1" title="Create a Department" id="dept-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>Departments</strong> in sidebar</li>
          <li>Click <strong>"+ Add Department"</strong></li>
          <li>Enter: Name (English), Name (Marathi), Department Type</li>
          <li>Set Head Name and Head Phone</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
      </StepCard>

      <h3>Staff Members</h3>
      <StepCard number="2" title="Add a Staff Member" id="staff-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Go to <strong>Staff Members</strong> in sidebar</li>
          <li>Click <strong>"+ Add Staff"</strong></li>
          <li>Enter: Full Name, Designation, Phone, Email</li>
          <li>Select Department and Category</li>
          <li>Click <strong>"Save"</strong></li>
        </ol>
        <InfoBox type="info">Staff members are linked to departments. Create departments first before adding staff.</InfoBox>
      </StepCard>
    </div>
  );
}

function UserApprovalsDoc({ toggleStep, expandedSteps }) {
  return (
    <div>
      <h2>🛡️ वापरकर्ता मंजुरी / User Approvals</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>Review and approve citizen registrations. New users must be verified before they can access the portal.</p>

      <h3>Registration → Approval Flow</h3>
      <WorkflowDiagram steps={[
        { label: 'Citizen Registers', desc: 'Fills registration form with Aadhaar card', color: '#9e9e9e' },
        { label: 'Pending Approval', desc: 'Account created but inactive', color: '#ff9800' },
        { label: 'Admin Reviews', desc: 'Verifies Aadhaar & details', color: '#2196f3' },
        { label: 'Approved / Rejected', desc: 'User can now login (if approved)', color: '#4caf50' },
      ]} />

      <StepCard number="1" title="Review Pending Users" id="ua-1" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Open <strong>User Approvals</strong> from sidebar</li>
          <li>View statistics: Pending, Approved, Rejected counts</li>
          <li>Click on <strong>"Pending"</strong> filter to see new registrations</li>
          <li>Click a user row to view full details including Aadhaar photo</li>
        </ol>
      </StepCard>

      <StepCard number="2" title="Approve a User" id="ua-2" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Open the user detail</li>
          <li>Verify the Aadhaar card photo matches the information</li>
          <li>Click <strong>"✅ Approve"</strong></li>
          <li>The user can now login to the citizen portal</li>
        </ol>
      </StepCard>

      <StepCard number="3" title="Reject a User" id="ua-3" toggleStep={toggleStep} expandedSteps={expandedSteps}>
        <ol>
          <li>Open the user detail</li>
          <li>Click <strong>"❌ Reject"</strong></li>
          <li>Enter the reason for rejection (required)</li>
          <li>Click <strong>"Confirm Rejection"</strong></li>
        </ol>
        <InfoBox type="warn">The rejection reason is stored and visible to admins. Please provide a clear reason.</InfoBox>
      </StepCard>
    </div>
  );
}

function RBACDoc() {
  return (
    <div>
      <h2>🔐 भूमिका व परवानग्या / Roles & Permissions</h2>
      <p style={{ color: '#555', marginBottom: 16 }}>The system uses Role-Based Access Control (RBAC) to manage what each user can do.</p>

      <h3>How It Works</h3>
      <ul>
        <li>Each user is assigned a <strong>Role</strong> (e.g., GP Admin, Clerk, Sarpanch)</li>
        <li>Each role has specific <strong>permissions</strong> for each module</li>
        <li>Permissions can also be set at the <strong>individual user</strong> level (overrides role)</li>
        <li>Sidebar navigation automatically hides modules the user cannot access</li>
      </ul>

      <h3>Permission Actions</h3>
      <FeatureTable rows={[
        ['View', 'See data and lists in a module'],
        ['Create', 'Add new records'],
        ['Update', 'Edit existing records and change statuses'],
        ['Delete', 'Remove records (where applicable)'],
      ]} />

      <h3>Permission Modules</h3>
      <FeatureTable rows={[
        ['Dashboard', 'View only'],
        ['Households', 'View, Create, Update, Delete'],
        ['Citizens', 'View, Create, Update, Delete'],
        ['Certificates', 'View, Create, Update, Delete'],
        ['Complaints', 'View, Create, Update, Delete'],
        ['Schemes', 'View, Create, Update, Delete'],
        ['Gramsabha', 'View, Create, Update, Delete'],
        ['Finance', 'View, Create, Update, Delete'],
        ['Projects', 'View, Create, Update, Delete'],
        ['Website Content', 'View, Create, Update, Delete'],
        ['Notices', 'View, Create, Update, Delete'],
        ['Facilities', 'View, Create, Update, Delete'],
        ['Businesses', 'View, Create, Update, Delete'],
        ['Departments', 'View, Create, Update, Delete'],
        ['Staff Members', 'View, Create, Update, Delete'],
        ['User Approvals', 'View, Update (approve/reject)'],
      ]} />

      <InfoBox type="important">Only <strong>Super Admin</strong> (System Admin) can create and manage roles and assign permissions. Contact your System Administrator to modify role permissions.</InfoBox>

      <h3>Built-in Roles</h3>
      <FeatureTable rows={[
        ['super_admin', 'Full unrestricted access to everything'],
        ['citizen', 'Public portal access only (no admin panel)'],
        ['Custom Roles', 'Created by Super Admin with specific permissions'],
      ]} />
    </div>
  );
}

/* ─── Styles ─── */

const styles = `
  .doc-page { max-width: 100%; }

  .doc-header {
    background: linear-gradient(135deg, #1a237e 0%, #1565c0 60%, #0288d1 100%);
    border-radius: 10px; padding: 16px 20px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; color: #fff; margin-bottom: 16px; flex-wrap: wrap;
  }
  .doc-header-left { display: flex; align-items: center; gap: 12px; }
  .doc-header-icon { font-size: 32px; }
  .doc-header h1 { margin: 0; font-size: 20px; font-weight: 700; }
  .doc-header p { margin: 2px 0 0; font-size: 12px; opacity: 0.8; }
  .doc-search-wrap { flex-shrink: 0; }
  .doc-search {
    padding: 8px 14px; border: none; border-radius: 6px;
    font-size: 13px; width: 220px; outline: none;
    background: rgba(255,255,255,0.95);
  }
  .doc-search:focus { box-shadow: 0 0 0 2px rgba(255,255,255,0.4); }

  .doc-layout { display: grid; grid-template-columns: 240px 1fr; gap: 16px; min-height: 500px; }

  .doc-nav {
    background: #fff; border: 1px solid #e0e0e0; border-radius: 10px;
    padding: 8px; display: flex; flex-direction: column; gap: 2px;
    position: sticky; top: 16px; align-self: start; max-height: calc(100vh - 140px); overflow-y: auto;
  }
  .doc-nav-btn {
    display: flex; align-items: center; gap: 8px; padding: 8px 10px;
    border: none; background: transparent; border-radius: 6px;
    cursor: pointer; font-size: 13px; text-align: left; transition: all 0.15s;
    color: #333; width: 100%;
  }
  .doc-nav-btn:hover { background: #f5f5f5; }
  .doc-nav-btn.active { background: #e3f2fd; color: #1565c0; font-weight: 600; }
  .doc-nav-icon { font-size: 16px; flex-shrink: 0; width: 20px; text-align: center; }
  .doc-nav-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .doc-content {
    background: #fff; border: 1px solid #e0e0e0; border-radius: 10px;
    padding: 24px; min-height: 400px;
  }
  .doc-content h2 { margin: 0 0 4px; font-size: 20px; color: #1a237e; }
  .doc-content h3 { margin: 16px 0 8px; font-size: 15px; color: #333; border-bottom: 1px solid #eee; padding-bottom: 4px; }
  .doc-content p { font-size: 13px; line-height: 1.5; }
  .doc-content ul, .doc-content ol { font-size: 13px; line-height: 1.7; padding-left: 20px; }
  .doc-content li { margin-bottom: 2px; }
  .doc-empty { text-align: center; color: #999; padding: 60px 20px; font-size: 15px; }

  .doc-step {
    border: 1px solid #e8e8e8; border-radius: 8px; margin: 10px 0;
    overflow: hidden;
  }
  .doc-step-header {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px; background: #fafafa; cursor: pointer;
    user-select: none; transition: background 0.15s;
  }
  .doc-step-header:hover { background: #f0f0f0; }
  .doc-step-num {
    background: #1565c0; color: #fff; width: 24px; height: 24px;
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; flex-shrink: 0;
  }
  .doc-step-title { font-weight: 600; font-size: 14px; flex: 1; }
  .doc-step-toggle { color: #999; font-size: 14px; }
  .doc-step-body { padding: 12px 14px 14px; font-size: 13px; }

  .doc-table {
    width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 13px;
  }
  .doc-table th {
    text-align: left; padding: 8px 10px; background: #f5f5f5;
    border-bottom: 2px solid #e0e0e0; font-size: 12px; color: #666;
  }
  .doc-table td {
    padding: 7px 10px; border-bottom: 1px solid #f0f0f0; vertical-align: top;
  }
  .doc-table tr:hover td { background: #fafafa; }

  .doc-workflow {
    background: #f8f9fa; border-radius: 8px; padding: 16px; margin: 10px 0;
    display: flex; flex-direction: column; gap: 4px;
  }
  .doc-wf-step { display: flex; align-items: flex-start; gap: 10px; }
  .doc-wf-dot {
    width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0;
    margin-top: 3px;
  }
  .doc-wf-step strong { font-size: 13px; }
  .doc-wf-desc { font-size: 12px; color: #666; }
  .doc-wf-arrow { text-align: center; color: #bbb; font-size: 14px; margin-left: 0; padding-left: 0; line-height: 1; }

  @media (max-width: 768px) {
    .doc-header { flex-direction: column; align-items: flex-start; }
    .doc-search { width: 100%; }
    .doc-layout { grid-template-columns: 1fr; }
    .doc-nav {
      position: static; max-height: none;
      flex-direction: row; flex-wrap: wrap; gap: 4px;
    }
    .doc-nav-btn { padding: 6px 8px; font-size: 12px; }
    .doc-nav-label { display: none; }
    .doc-nav-icon { font-size: 18px; width: auto; }
    .doc-content { padding: 16px; }
    .doc-content h2 { font-size: 17px; }
  }
`;
